import React, { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Table from 'react-bootstrap/Table';
import ListGroup from 'react-bootstrap/ListGroup';

function SupplierReferences({ articleId, articleName }) {
    // Estado para la lista de proveedores asociados al artículo
    const [supplierReferences, setSupplierReferences] = useState([]);
    
    // Estado para el formulario de nuevo proveedor
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [referenceValue, setReferenceValue] = useState('');
    const [descriptionValue, setDescriptionValue] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Mock de todos los proveedores disponibles para el buscador
    const allSuppliersMock = [
        { id: 101, name: 'Pescanova' },
        { id: 102, name: 'Makro' },
        { id: 103, name: 'Mercadona' },
        { id: 104, name: 'Carrefour' },
        { id: 105, name: 'Lidl' },
        { id: 106, name: 'Aldi' },
        { id: 107, name: 'Alcampo' },
        { id: 108, name: 'Eroski' },
    ];

    const isEnabled = !!articleId;

    // Cargar datos simulados cuando cambia el articleId
    useEffect(() => {
        if (articleId) {
            console.log(`Cargando proveedores para artículo ID: ${articleId}, Nombre: ${articleName || 'Desconocido'}`);
            // Mock de datos existentes
            setSupplierReferences([
                { id: 1, supplierId: 101, supplierName: 'Pescanova', reference: 'PES-001', description: 'Proveedor principal de congelados' },
                { id: 2, supplierId: 102, supplierName: 'Makro', reference: 'MK-999', description: 'Compras mayoristas' }
            ]);
        } else {
            setSupplierReferences([]);
        }
        // Reset form
        setSearchTerm('');
        setSelectedSupplier(null);
        setReferenceValue('');
        setDescriptionValue('');
        setEditingId(null);
    }, [articleId, articleName]);

    // Manejador del buscador
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setSelectedSupplier(null); // Resetear selección si escribe de nuevo
        setShowSuggestions(true);
    };

    const handleSelectSupplier = (supplier) => {
        setSearchTerm(supplier.name);
        setSelectedSupplier(supplier);
        setShowSuggestions(false);
    };

    // Filtrar sugerencias
    const filteredSuppliers = allSuppliersMock.filter(s => 
        s.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Añadir o actualizar proveedor en la lista
    const handleSaveSupplierReference = () => {
        if (selectedSupplier && referenceValue.trim() !== '') {
            if (editingId) {
                // Actualizar existente
                setSupplierReferences(supplierReferences.map(item => 
                    item.id === editingId 
                        ? { 
                            ...item, 
                            supplierId: selectedSupplier.id, 
                            supplierName: selectedSupplier.name, 
                            reference: referenceValue,
                            description: descriptionValue
                          }
                        : item
                ));
                setEditingId(null);
            } else {
                // Añadir nuevo
                const newEntry = {
                    id: Date.now(), // ID temporal
                    supplierId: selectedSupplier.id,
                    supplierName: selectedSupplier.name,
                    reference: referenceValue,
                    description: descriptionValue
                };
                setSupplierReferences([...supplierReferences, newEntry]);
            }
            
            // Resetear formulario
            setSearchTerm('');
            setSelectedSupplier(null);
            setReferenceValue('');
            setDescriptionValue('');
        }
    };

    // Cargar datos en el formulario para editar
    const handleEdit = (item) => {
        setEditingId(item.id);
        setSearchTerm(item.supplierName);
        // Intentar encontrar el proveedor en el mock, o usar los datos del item
        const supplier = allSuppliersMock.find(s => s.id === item.supplierId) || { id: item.supplierId, name: item.supplierName };
        setSelectedSupplier(supplier);
        setReferenceValue(item.reference);
        setDescriptionValue(item.description || '');
    };

    // Cancelar edición
    const handleCancelEdit = () => {
        setEditingId(null);
        setSearchTerm('');
        setSelectedSupplier(null);
        setReferenceValue('');
        setDescriptionValue('');
    };

    // Eliminar proveedor de la lista
    const handleDelete = (id) => {
        console.log("Eliminando relación proveedor con ID:", id);
        setSupplierReferences(supplierReferences.filter(ref => ref.id !== id));
    };

    return (
        <Container className="mt-3">
            {isEnabled && supplierReferences.length > 0 && (
                <div className="mb-4">
                    <h5>Proveedores Asociados</h5>
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Proveedor</th>
                                <th>Referencia</th>
                                <th>Descripción</th>
                                <th style={{ width: '120px' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {supplierReferences.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.supplierName}</td>
                                    <td>{item.reference}</td>
                                    <td>{item.description}</td>
                                    <td>
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm" 
                                            className="me-2"
                                            onClick={() => handleEdit(item)}
                                            title="Editar"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </Button>
                                        <Button 
                                            variant="outline-danger" 
                                            size="sm" 
                                            onClick={() => handleDelete(item.id)}
                                            title="Eliminar"
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            <h5 className="mb-3">{editingId ? 'Editar Proveedor' : `Añadir Proveedor a ${articleName || 'Desconocido'}`}</h5>
            <Form>
                <Row className="mb-3">
                    <Col md={4} className="position-relative">
                        <Form.Group controlId="supplierSearch">
                            <Form.Label>Buscar Proveedor</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={isEnabled ? "Escribe para buscar..." : "Seleccione un artículo primero"}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                disabled={!isEnabled}
                                autoComplete="off"
                            />
                            {/* Lista de sugerencias (simulando dropdown) */}
                            {showSuggestions && searchTerm && filteredSuppliers.length > 0 && (
                                <ListGroup className="position-absolute w-100" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
                                    {filteredSuppliers.map(s => (
                                        <ListGroup.Item 
                                            key={s.id} 
                                            action 
                                            onClick={() => handleSelectSupplier(s)}
                                        >
                                            {s.name}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="supplierReference">
                            <Form.Label>Referencia del Proveedor</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Referencia..."
                                value={referenceValue}
                                onChange={(e) => setReferenceValue(e.target.value)}
                                disabled={!isEnabled || !selectedSupplier}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="supplierDescription">
                            <Form.Label>Descripción</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Descripción opcional..."
                                value={descriptionValue}
                                onChange={(e) => setDescriptionValue(e.target.value)}
                                disabled={!isEnabled || !selectedSupplier}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                
                <div className="mt-2">
                    {editingId ? (
                        <>
                            <Button 
                                variant="success" 
                                onClick={handleSaveSupplierReference}
                                disabled={!isEnabled || !selectedSupplier || !referenceValue}
                                className="me-2"
                            >
                                <i className="fas fa-save me-1"></i> Actualizar
                            </Button>
                            <Button 
                                variant="secondary" 
                                onClick={handleCancelEdit}
                            >
                                Cancelar
                            </Button>
                        </>
                    ) : (
                        <Button 
                            variant="outline-success" 
                            onClick={handleSaveSupplierReference}
                            disabled={!isEnabled || !selectedSupplier || !referenceValue}
                        >
                            <i className="fas fa-plus"></i>
                        </Button>
                    )}
                </div>
            </Form>
        </Container>
    );
}

export default SupplierReferences;